/**
 * Alertes de la barre système (licence, SMS, adresse e-mail) : ce qu'il faut
 * dire, et avec quelle gravité. Logique pure, testée seule ; l'affichage est
 * dans `SystemAlertsBar`.
 *
 * Principe : un espace qui vient d'être créé ne doit voir aucune alerte
 * anxiogène. Un essai gratuit est une information, pas une licence qui
 * expire ; un solde SMS nul n'est un problème que si la clinique a activé
 * les rappels automatiques.
 */
import type { MeResponse } from "./types";

export type AlertSeverity = "info" | "warning" | "critical";

export type SystemAlert =
  | { kind: "email"; key: string; severity: AlertSeverity; email: string }
  | {
      kind: "license";
      key: string;
      severity: AlertSeverity;
      trial: boolean;
      inGrace: boolean;
      daysRemaining: number;
    }
  | { kind: "sms"; key: string; severity: AlertSeverity; disabled: boolean };

/** À partir de combien de jours restants un essai passe de l'information à l'attention. */
export const TRIAL_WARNING_DAYS = 7;
/** Fenêtre de rappel avant l'échéance d'une licence payée. */
export const LICENSE_REMINDER_DAYS = 30;

export function systemAlerts(me: MeResponse | null | undefined): SystemAlert[] {
  if (!me) return [];
  const alerts: SystemAlert[] = [];

  if (me.email_verified === false) {
    alerts.push({ kind: "email", key: `email.unverified.${me.id}`, severity: "warning", email: me.email });
  }

  const lic = me.license;
  if (lic && lic.is_valid) {
    const trial = lic.plan === "trial";
    const show = lic.in_grace || trial || lic.days_remaining <= LICENSE_REMINDER_DAYS;
    if (show) {
      const severity: AlertSeverity = lic.in_grace
        ? "critical"
        : trial && lic.days_remaining > TRIAL_WARNING_DAYS
          ? "info"
          : "warning";
      // La clé change avec la gravité : une alerte masquée revient si la
      // situation s'aggrave (fin d'essai proche, période de grâce).
      alerts.push({
        kind: "license",
        key: `license.${lic.status}.${trial ? "trial" : "paid"}.${severity}`,
        severity,
        trial,
        inGrace: lic.in_grace,
        daysRemaining: lic.days_remaining,
      });
    }
  }

  const sms = me.active_sms;
  if (sms && sms.state !== "ok") {
    const disabled = sms.state === "disabled";
    // Solde nul sans rappels activés : rien de ce que la clinique a demandé
    // n'est empêché, on ne dit rien. `undefined` (ancien serveur) : on prévient.
    if (disabled || sms.reminders_enabled !== false) {
      alerts.push({ kind: "sms", key: `sms.${sms.state}`, severity: "critical", disabled });
    }
  }

  return alerts;
}

const RANK: Record<AlertSeverity, number> = { info: 0, warning: 1, critical: 2 };

/** Gravité de la barre : celle de l'alerte la plus grave encore affichée. */
export function highestSeverity(alerts: readonly SystemAlert[]): AlertSeverity | null {
  return alerts.reduce<AlertSeverity | null>(
    (worst, a) => (worst === null || RANK[a.severity] > RANK[worst] ? a.severity : worst),
    null,
  );
}
